import { useState, useEffect } from 'react';
import AppManagement from '../components/admin/AppManagement';
import RequestManagement from '../components/admin/RequestManagement';
import ReviewManagement from '../components/admin/ReviewManagement';
import CreateApp from '../components/admin/CreateApp';
import AppStoreManagement from '../components/admin/AppStoreManagement';
import { useAppStore } from '../services/store';

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState('appstores');
  const [dashboardFilter, setDashboardFilter] = useState('');
  const [activeMetric, setActiveMetric] = useState('');
  const { apps, requests, loadInitialData } = useAppStore();

  // Load initial data when component mounts
  useEffect(() => {
    loadInitialData();
  }, []);

  // Sample app stores data with assigned clients
  const appStores = [
    {
      id: '1',
      name: 'Municipal AppStore',
      status: 'Active',
      totalApps: 15,
      activeApps: 12,
      inactiveApps: 3,
      pendingRequests: 3,
      pendingReviews: 2,
      activeSubscriptions: 45,
      activeTrials: 8,
      assignedClients: ['City of Springfield', 'Metro Municipality', 'County Government']
    },
    {
      id: '2',
      name: 'General AppStore',
      status: 'Active',
      totalApps: 21,
      activeApps: 18,
      inactiveApps: 3,
      pendingRequests: 5,
      pendingReviews: 1,
      activeSubscriptions: 89,
      activeTrials: 12,
      assignedClients: ['TechCorp Inc', 'StartupHub', 'Enterprise Solutions', 'Digital Agency']
    },
    {
      id: '3',
      name: 'Medical AppStore',
      status: 'Inactive',
      totalApps: 8,
      activeApps: 5,
      inactiveApps: 3,
      pendingRequests: 1,
      pendingReviews: 2,
      activeSubscriptions: 23,
      activeTrials: 3,
      assignedClients: ['Metro Health Dept', 'Regional Hospital']
    }
  ];

  // Calculate dashboard statistics
  const stats = {
    appstores: {
      active: appStores.filter(store => store.status === 'Active').length,
      inactive: appStores.filter(store => store.status === 'Inactive').length,
    },
    apps: {
      total: apps.length,
      active: apps.filter(app => app.isActive).length,
      inactive: apps.filter(app => !app.isActive).length,
    },
    requests: {
      total: requests.length,
      pending: requests.filter(r => r.status === 'Pending').length,
      approved: requests.filter(r => r.status === 'Approved').length,
      rejected: requests.filter(r => r.status === 'Rejected').length,
    },
    reviews: {
      pending: 5,
      accepted: 15,
      rejected: 2,
    },
    trial: {
      active: apps.filter(app => app.trialExpiresAt && new Date(app.trialExpiresAt) > new Date()).length,
      expired: apps.filter(app => app.trialExpiresAt && new Date(app.trialExpiresAt) <= new Date()).length,
      purchased: apps.filter(app => app.accessType === 'Paid').length,
    },
    subscription: {
      total: 150,
      active: 120,
      aboutToExpire: 8,
      expired: 22,
    }
  };

  const handleCardClick = (tab: string, filter: string, metricKey: string) => {
    setActiveTab(tab);
    setDashboardFilter(filter);
    setActiveMetric(metricKey);
  };

  const dashboardCards = [
    {
      title: 'App Stores',
      data: [
        { label: 'Total', value: appStores.length, color: 'text-blue-600', tab: 'appstores', filter: '', key: 'appstores-total' },
        { label: 'Active', value: stats.appstores.active, color: 'text-green-600', tab: 'appstores', filter: 'active', key: 'appstores-active' },
        { label: 'Inactive', value: stats.appstores.inactive, color: 'text-red-600', tab: 'appstores', filter: 'inactive', key: 'appstores-inactive' },
      ]
    },
    {
      title: 'Apps',
      data: [
        { label: 'Total', value: stats.apps.total, color: 'text-blue-600', tab: 'apps', filter: 'all', key: 'apps-total' },
        { label: 'Active', value: stats.apps.active, color: 'text-green-600', tab: 'apps', filter: 'active', key: 'apps-active' },
        { label: 'Inactive', value: stats.apps.inactive, color: 'text-red-600', tab: 'apps', filter: 'inactive', key: 'apps-inactive' },
      ]
    },
    {
      title: 'App Requests',
      data: [
        { label: 'Total', value: stats.requests.total, color: 'text-purple-600', tab: 'requests', filter: 'all', key: 'requests-total' },
        { label: 'Pending', value: stats.requests.pending, color: 'text-yellow-600', tab: 'requests', filter: 'pending', key: 'requests-pending' },
        { label: 'Approved', value: stats.requests.approved, color: 'text-green-600', tab: 'requests', filter: 'approved', key: 'requests-approved' },
        { label: 'Rejected', value: stats.requests.rejected, color: 'text-red-600', tab: 'requests', filter: 'rejected', key: 'requests-rejected' },
      ]
    },
    {
      title: 'Reviews',
      data: [
        { label: 'Pending', value: stats.reviews.pending, color: 'text-yellow-600', tab: 'reviews', filter: 'pending', key: 'reviews-pending' },
        { label: 'Accepted', value: stats.reviews.accepted, color: 'text-green-600', tab: 'reviews', filter: 'accepted', key: 'reviews-accepted' },
        { label: 'Rejected', value: stats.reviews.rejected, color: 'text-red-600', tab: 'reviews', filter: 'rejected', key: 'reviews-rejected' },
      ]
    },
    {
      title: 'Trial Status',
      data: [
        { label: 'Active', value: stats.trial.active, color: 'text-green-600', tab: 'apps', filter: 'trial-active', key: 'trial-active' },
        { label: 'Expired', value: stats.trial.expired, color: 'text-red-600', tab: 'apps', filter: 'trial-expired', key: 'trial-expired' },
        { label: 'Purchased', value: stats.trial.purchased, color: 'text-blue-600', tab: 'apps', filter: 'purchased', key: 'trial-purchased' },
      ]
    },
    {
      title: 'Subscriptions',
      data: [
        { label: 'Total', value: stats.subscription.total, color: 'text-indigo-600', tab: 'apps', filter: 'subscription-all', key: 'subscription-total' },
        { label: 'Active', value: stats.subscription.active, color: 'text-green-600', tab: 'apps', filter: 'subscription-active', key: 'subscription-active' },
        { label: 'About to Expire', value: stats.subscription.aboutToExpire, color: 'text-orange-600', tab: 'apps', filter: 'subscription-expiring', key: 'subscription-expiring' },
        { label: 'Expired', value: stats.subscription.expired, color: 'text-red-600', tab: 'apps', filter: 'subscription-expired', key: 'subscription-expired' },
      ]
    }
  ];

  const tabs = [
    { id: 'appstores', name: 'App Stores', icon: 'ri-store-3-line', count: appStores.length },
    { id: 'apps', name: 'Apps', icon: 'ri-store-2-line', count: apps.length },
    { id: 'requests', name: 'App Requests', icon: 'ri-file-list-3-line', count: requests.filter(r => r.status === 'Pending').length },
    { id: 'reviews', name: 'Reviews', icon: 'ri-star-line', count: stats.reviews.pending },
    { id: 'trial', name: 'Trial Status', icon: 'ri-time-line', count: null },
    { id: 'subscription', name: 'Subscriptions', icon: 'ri-calendar-check-line', count: null },
    { id: 'create', name: 'Create App', icon: 'ri-add-circle-line', count: null }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Admin Panel</h1>
          <p className="text-lg text-gray-600">Manage your App Store, requests, and reviews</p>
        </div>

        {/* Dashboard Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dashboardCards.map((card, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                  <h3 className="font-semibold text-gray-900 text-lg text-center">{card.title}</h3>
                </div>
                <div className="p-6">
                  <div className={`grid gap-4 ${card.data.length === 4 ? 'grid-cols-4' : card.data.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                    {card.data.map((item, itemIndex) => (
                      <button
                        key={itemIndex}
                        onClick={() => handleCardClick(item.tab, item.filter, item.key)}
                        className={`flex flex-col items-center p-4 rounded-lg transition-all cursor-pointer group ${
                          activeMetric === item.key
                            ? 'bg-blue-50 border-2 border-blue-200 shadow-sm'
                            : 'hover:bg-gray-50 border-2 border-transparent'
                        }`}
                      >
                        <span className={`text-3xl font-bold mb-1 transition-colors ${
                          activeMetric === item.key
                            ? 'text-blue-600'
                            : `${item.color} group-hover:text-blue-600`
                        }`}>
                          {item.value}
                        </span>
                        <span className={`text-sm text-center font-medium ${
                          activeMetric === item.key
                            ? 'text-blue-700'
                            : item.color
                        }`}>
                          {item.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <i className={`${tab.icon} mr-2 text-lg`}></i>
                  {tab.name}
                  {tab.count !== null && tab.count > 0 && (
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                      activeTab === tab.id
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'appstores' && <AppStoreManagement appStores={appStores} filter={dashboardFilter} />}
            {activeTab === 'apps' && <AppManagement filter={dashboardFilter} />}
            {activeTab === 'requests' && <RequestManagement filter={dashboardFilter} />}
            {activeTab === 'reviews' && <ReviewManagement filter={dashboardFilter} />}
            {(activeTab === 'trial' || activeTab === 'subscription') && (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${activeTab === 'trial' ? 'ri-time-line' : 'ri-calendar-check-line'} text-2xl text-purple-600`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {activeTab === 'trial' ? 'Trial Management' : 'Subscription Management'}
                </h3>
                <p className="text-gray-600">
                  {activeTab === 'trial'
                    ? 'Monitor and manage app trial periods and conversions'
                    : 'Manage subscription plans, renewals, and billing cycles'
                  }
                </p>
              </div>
            )}
            {activeTab === 'create' && <CreateApp />}
          </div>
        </div>
      </div>
    </div>
  );
}
